<template>
  <el-drawer
    v-model="visible"
    :close-on-click-modal="closeDrawerOnOutsideClick"
    :modal="false"
    :append-to-body="false"
    :fullscreen="false"
    :size="drawerWidthPct"
    :class="{'link-tasks-drawer__existing-tasks-panel-on' : !showAddTaskPanel }"
    destroy-on-close
    class="link-tasks-drawer"
    :modal-class="showAddTaskPanel ? 'link-tasks-drawer' : 'link-tasks-drawer link-tasks-drawer__existing-tasks-panel-on'"
    @opened="onOpened"
    @closed="onClosed"
  >
    <template #header>
      <div v-if="drawerOpened" class="link-tasks-drawer__header">
        <h1>Link Task</h1>
        <h2 v-if="originatingConversation">
          <i class="ddi ddi-conversation" />
          #{{originatingConversation.refNumber}} {{originatingConversation.title}}
        </h2>
        <h2 v-else-if="originatingTask">
          <i class="ddi ddi-task" />
          #{{originatingTask.refNumber}} {{originatingTask.title}}
        </h2>

        <!-- NAV -->
        <div class="link-tasks-drawer__nav">
          <div :class="{'selected': showAddTaskPanel}" @click="showAddTaskPanel = true">Create New</div>
          <div
            :class="{'selected': !showAddTaskPanel}"
            @click="showAddTaskPanel = false"
          >Link Existing</div>
        </div>
      </div>
    </template>
    <template v-if="drawerOpened">
      <!-- ADD GLOBAL TASK -->
      <add-global-task
        v-show="showAddTaskPanel"
        :originating-conversation="originatingConversation"
        :originating-task="originatingTask"
        :originating-item-event-id="originatingItemEventId"
        :default-project-id="defaultProjectId"
        lives-in-drawer
        @task-created="(projectID, id, createAnother) => handleTaskCreated(createAnother)"
        @task-desc-modified="closeDrawerOnOutsideClick = false"
        @task-desc-reset="closeDrawerOnOutsideClick = true"
        @canceled="handleCancel()"
      />

      <!-- LINK EXISTING TASKS -->
      <link-existing-tasks-panel
        v-show="!showAddTaskPanel"
        :default-project-id="defaultProjectId"
        :originating-task="originatingTask"
        :currently-linked-tasks="currentlyLinkedTasks"
        @canceled="handleCancel()"
        @selected-existing-task-id="(id) => $emit('selected-existing-task-id', id)"
      />
    </template>
  </el-drawer>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import {
  IItemReference,
  ILinkedItemForListing,
} from '@/api/types/response.types'

export default defineComponent({
  name: 'LinkTasksDrawer',

  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
    currentlyLinkedTasks: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },
    originatingConversation: {
      type: Object as () => IItemReference,
      default: null,
    },
    originatingTask: {
      type: Object as () => IItemReference,
      default: null,
    },
    originatingItemEventId: {
      type: Number,
      default: null,
    },
    // Default project to select after projects dropdown is loaded
    defaultProjectId: {
      type: Number,
      default: null,
    },
  },

  emits: ['selected-existing-task-id', 'task-created', 'update:showDrawer'],

  data() {
    return {
      isLoading: false,
      closeDrawerOnOutsideClick: true,
      drawerOpened: false,
      showAddTaskPanel: true,
    }
  },

  computed: {
    visible: {
      get(): boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '100%'
      }
      return '680px'
    },
  },

  methods: {
    onOpened() {
      this.drawerOpened = true
    },

    onClosed() {
      this.drawerOpened = false
    },

    handleTaskCreated(createAnother: boolean) {
      this.$emit('task-created')
      if (!createAnother) {
        this.handleCancel()
      }
    },

    handleCancel() {
      this.$emit('update:showDrawer', false)
    },
  },
})
</script>

<style lang="scss">
.link-tasks-drawer {
  /* Default drawer overrides */
  .el-drawer__header {
    padding-bottom: 0;
  }

  &__existing-tasks-panel-on .el-drawer__body {
    background-color: var(--color-background-secondary);
  }

  &__header {
    display: flex;
    padding: 20px 0 0 20px;
    flex-direction: column;
    align-items: flex-start;

    > h1 {
      font-size: 22px !important;
      font-weight: 500 !important;
      margin-bottom: 4px !important;
    }

    > h2 {
      @include lettering--p-sm;
      color: var(--color-text-third);
      @include single-line-ellipsis;
      max-width: 420px;

      @include media-breakpoint-down(sm) {
        max-width: 360px;
      }
    }
  }

  &__nav {
    display: flex;
    padding-top: 5px;

    > div {
      @include lettering--p-sm;
      font-weight: $bold;
      padding: 6px 0;
      margin: 0 12px;
      display: inline-block;
      color: var(--color-text-third);
      cursor: pointer;

      &:first-child {
        margin-left: 0;
      }

      &.selected {
        color: var(--color-text-secondary);
        border-bottom: solid 2px $projects--theme-color;
      }
    }
  }
}
</style>
